import React, { useState, useEffect } from 'react';
import { Menu, Plus, Settings as SettingsIcon, MessageSquare, Trash2, Edit2, Check, X, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import SettingsModal from './components/SettingsModal';
import { AppSettings, ModelType, AppLanguage, ChatMessage, Conversation, TRANSLATIONS, ToastMessage } from './types';

// Nular Logo Component
const NularLogo = () => (
  <div className="relative group cursor-pointer flex items-center justify-center w-full h-full">
    <div className="w-8 h-8 flex items-center justify-center">
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-nular-silver" xmlns="http://www.w3.org/2000/svg">
         <g className="origin-center animate-spin-slow">
             <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
         </g>
         <path d="M10.8613 9.36335C11.3679 8.45445 11.6213 8 12 8C12.3787 8 12.6321 8.45445 13.1387 9.36335L13.2698 9.59849C13.4138 9.85677 13.4858 9.98591 13.598 10.0711C13.7103 10.1563 13.8501 10.188 14.1296 10.2512L14.3842 10.3088C15.3681 10.5314 15.86 10.6427 15.977 11.0191C16.0941 11.3955 15.7587 11.7876 15.088 12.572L14.9144 12.7749C14.7238 12.9978 14.6285 13.1092 14.5857 13.2471C14.5428 13.385 14.5572 13.5336 14.586 13.831L14.6122 14.1018C14.7136 15.1482 14.7644 15.6715 14.4579 15.9041C14.1515 16.1367 13.6909 15.9246 12.7697 15.5005L12.5314 15.3907C12.2696 15.2702 12.1387 15.2099 12 15.2099C11.8613 15.2099 11.7304 15.2702 11.4686 15.3907L11.2303 15.5005C10.3091 15.9246 9.84847 16.1367 9.54206 15.9041C9.23565 15.6715 9.28635 15.1482 9.38776 14.1018L9.41399 13.831C9.44281 13.5336 9.45722 13.385 9.41435 13.2471C9.37147 13.1092 9.27617 12.9978 9.08557 12.7749L8.91204 12.572C8.2413 11.7876 7.90593 11.3955 8.02297 11.0191C8.14001 10.6427 8.63194 10.5314 9.61581 10.3088L9.87035 10.2512C10.1499 10.188 10.2897 10.1563 10.402 10.0711C10.5142 9.98591 10.5862 9.85677 10.7302 9.59849L10.8613 9.36335Z" stroke="currentColor" strokeWidth="1.5"></path>
      </svg>
    </div>
  </div>
);

const App: React.FC = () => {
  // State Initialization with LocalStorage
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('nular_settings');
    return saved ? JSON.parse(saved) : {
      model: ModelType.NULAR_FLASH,
      language: AppLanguage.ENGLISH,
      enableThinking: false,
      enableDeepResearch: false,
      userMemory: ''
    };
  });
  
  const [isSidebarOpen, setSidebarOpen] = useState(false); // Default closed as requested
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  
  // Chat State Initialization with LocalStorage
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const saved = localStorage.getItem('nular_conversations');
    return saved ? JSON.parse(saved) : [{
      id: '1', title: 'New Conversation', messages: [], updatedAt: Date.now()
    }];
  });
  
  const [activeConvId, setActiveConvId] = useState<string>(() => {
     return conversations[0]?.id || '1';
  });

  const [editingConvId, setEditingConvId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  // Persistence Effects
  useEffect(() => {
    localStorage.setItem('nular_settings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem('nular_conversations', JSON.stringify(conversations));
  }, [conversations]);

  // Helpers
  const activeConversation = conversations.find(c => c.id === activeConvId) || conversations[0];
  const t = TRANSLATIONS[settings.language];

  // Toast Function
  const showToast = (title: string, description: string = "", type: 'info'|'success'|'error' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, title, description, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  // Global Clipboard Listener for Toast
  useEffect(() => {
    const handleCopy = () => showToast("Success", "Copied to clipboard successfully", 'success');
    window.addEventListener('copy', handleCopy);
    return () => {
      window.removeEventListener('copy', handleCopy);
    };
  }, []);

  // Mouse Cursor Effect - Fast, No Lerp
  useEffect(() => {
    const cursor = document.createElement('div');
    cursor.classList.add('custom-cursor');
    document.body.appendChild(cursor);

    const moveCursor = (e: MouseEvent) => {
      cursor.style.left = `${e.clientX}px`;
      cursor.style.top = `${e.clientY}px`;
      
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button') || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.classList.contains('clickable')) {
        cursor.classList.add('hovering');
      } else {
        cursor.classList.remove('hovering');
      }
    };

    window.addEventListener('mousemove', moveCursor);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      if(document.body.contains(cursor)) document.body.removeChild(cursor);
    };
  }, []);

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const createNewChat = () => {
    const newId = Date.now().toString();
    setConversations(prev => [{
      id: newId,
      title: 'New Chat',
      messages: [],
      updatedAt: Date.now()
    }, ...prev]);
    setActiveConvId(newId);
    // Auto expand if needed or keep minimal
  };

  const deleteChat = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (conversations.length <= 1) {
      showToast("Error", "Cannot delete last conversation", 'error');
      return;
    }
    const newConvs = conversations.filter(c => c.id !== id);
    setConversations(newConvs);
    if (activeConvId === id) setActiveConvId(newConvs[0].id);
    showToast("Success", "Conversation deleted successfully", 'success');
  };

  const startRename = (id: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConvId(id);
    setEditTitle(currentTitle);
  };

  const saveRename = (e: React.MouseEvent) => {
     e.stopPropagation();
     setConversations(prev => prev.map(c => c.id === editingConvId ? { ...c, title: editTitle } : c));
     setEditingConvId(null);
     showToast("Success", "Conversation renamed successfully", 'success');
  };

  const setMessages = (fn: (prev: ChatMessage[]) => ChatMessage[]) => {
    setConversations(prev => prev.map(c => {
      if (c.id === activeConvId) {
        return { ...c, messages: fn(c.messages) };
      }
      return c;
    }));
  };

  return (
    <div className="h-screen w-screen bg-nular-base text-nular-silver flex overflow-hidden font-sans">
      
      {/* Sidebar */}
      <aside className={`${isSidebarOpen ? 'w-72' : 'w-16'} transition-all duration-300 ease-in-out h-full border-r border-nular-border bg-nular-panel flex flex-col z-30`}>
        
        {/* Toggle Button / Logo */}
        <div className="p-4 flex items-center justify-center h-16 border-b border-nular-border/30">
          <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 rounded-lg hover:bg-nular-border transition-colors">
             <NularLogo />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="px-3 pt-4 pb-2">
          <button 
            onClick={createNewChat}
            className={`w-full flex items-center justify-center p-3 bg-nular-silver text-black rounded-lg hover:bg-white transition-all shadow-md group ${isSidebarOpen ? 'gap-2' : ''}`}
          >
            <Plus size={20} className="transition-transform group-hover:rotate-90"/>
            {isSidebarOpen && <span className="font-medium">{t.newChat}</span>}
          </button>
        </div>

        {/* History List */}
        <div className="flex-1 overflow-y-auto px-2 space-y-1 mt-2">
           {isSidebarOpen && <div className="text-[10px] text-nular-dim font-bold tracking-wider uppercase mb-2 px-3 opacity-60">History</div>}
           {conversations.map(conv => (
             <div
               key={conv.id}
               onClick={() => { setActiveConvId(conv.id); }}
               className={`group w-full relative flex items-center rounded-lg cursor-pointer transition-all ${
                 activeConvId === conv.id ? 'bg-nular-border/80 text-nular-silver' : 'text-nular-dim hover:text-nular-silver hover:bg-nular-border/40'
               } ${isSidebarOpen ? 'px-3 py-3' : 'justify-center py-3'}`}
             >
               {!isSidebarOpen ? (
                  <MessageSquare size={18} />
               ) : (
                 <>
                   <MessageSquare size={16} className="flex-shrink-0 mr-3 opacity-70" />
                   
                   {editingConvId === conv.id ? (
                      <div className="flex items-center flex-1 min-w-0 gap-1">
                         <input 
                           value={editTitle}
                           onChange={(e) => setEditTitle(e.target.value)}
                           onClick={(e) => e.stopPropagation()}
                           className="bg-black/50 border border-nular-dim/50 rounded px-1 py-0.5 text-xs w-full text-white"
                           autoFocus
                         />
                         <button onClick={saveRename} className="p-1 text-green-400 hover:bg-green-900/30 rounded"><Check size={12}/></button>
                         <button onClick={(e) => { e.stopPropagation(); setEditingConvId(null); }} className="p-1 text-red-400 hover:bg-red-900/30 rounded"><X size={12}/></button>
                      </div>
                   ) : (
                      <span className="truncate text-sm font-medium flex-1">
                        {conv.messages.length > 0 ? conv.messages[0].text.substring(0, 20) + (conv.messages[0].text.length > 20 ? '...' : '') : conv.title}
                      </span>
                   )}
                   
                   {/* Action Buttons */}
                   {isSidebarOpen && editingConvId !== conv.id && (
                     <div className="absolute right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-nular-panel/80 rounded pl-2">
                        <button onClick={(e) => startRename(conv.id, conv.title, e)} className="p-1.5 hover:text-blue-400 hover:bg-nular-border rounded"><Edit2 size={12} /></button>
                        <button onClick={(e) => deleteChat(conv.id, e)} className="p-1.5 hover:text-red-400 hover:bg-nular-border rounded"><Trash2 size={12} /></button>
                     </div>
                   )}
                 </>
               )}
             </div>
           ))}
        </div>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-nular-border bg-nular-panel">
          <button 
            onClick={() => setSettingsOpen(true)}
            className={`w-full flex items-center ${isSidebarOpen ? 'justify-start px-3 gap-3' : 'justify-center'} py-3 text-nular-dim hover:text-nular-silver hover:bg-nular-border rounded-lg transition-all`}
          >
            <SettingsIcon size={20} />
            {isSidebarOpen && <span>{t.settings}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full relative">
        <ChatInterface 
          settings={settings}
          messages={activeConversation.messages}
          setMessages={setMessages}
          updateSettings={updateSettings}
          showToast={showToast}
        />
        
        {/* Toast Container - Top Right Squared */}
        <div className="absolute top-5 right-5 z-50 flex flex-col gap-3 pointer-events-none">
           {toasts.map(toast => (
              <div key={toast.id} className="animate-slide-in pointer-events-auto bg-nular-panel border border-nular-border text-nular-silver p-4 rounded-none shadow-2xl flex items-start gap-3 min-w-[300px]">
                 <div className={`mt-0.5 p-1 ${toast.type === 'success' ? 'text-green-500' : toast.type === 'error' ? 'text-red-500' : 'text-blue-500'}`}>
                    {toast.type === 'success' ? <CheckCircle size={18}/> : toast.type === 'error' ? <AlertTriangle size={18}/> : <Info size={18}/>}
                 </div>
                 <div className="flex-1">
                    <h4 className="text-sm font-bold text-white">{toast.title}</h4>
                    {toast.description && <p className="text-xs text-nular-dim mt-1">{toast.description}</p>}
                 </div>
                 <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))} className="text-nular-dim hover:text-white">
                    <X size={14}/>
                 </button>
              </div>
           ))}
        </div>
      </main>

      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setSettingsOpen(false)} 
        settings={settings}
        updateSettings={updateSettings}
      />
      
    </div>
  );
};

export default App;